import {
  Alert,
  AnimatedContentLoader,
  Button,
  Input,
  Prompt,
  Tab,
  Tabs,
  ToggleContainer
} from '@components'
import { HeaderBlock } from '@components/HeaderBlock'
import { getDocumentsPageUrl, ReportType } from '@constants'
import {
  FeatureWrapper,
  PermissionBoundary,
  PermissionSet,
  useDateRangeContext,
  usePermission
} from '@context'
import { deletePeriod, fetchPeriod, IPeriod, performPeriodAction, updatePeriod } from '@query'
import { formatDate, setAPIErrors } from '@utils'
import { downloadCsvReport, downloadDocxReport, generateReport } from '@utils/generateReport'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { FaCalendarAlt, FaCheckCircle, FaTrash, FaUnlock } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { FinancialStatementForm } from './FinancialStatementForm'
import { DateBlock, DetailContainer } from './PeriodDetails.styled'
import { PeriodSteps } from './PeriodSteps'
import { DatevPeriodComingSoon } from './DatevPeriodComingSoon'
import { useHistory } from 'react-router-dom'

interface Props {
  periodId: number
  slug: string
  onClose: () => void
}

export const PeriodDetails: React.FC<Props> = ({ periodId, slug, onClose }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const history = useHistory()
  const [showDelete, setShowDelete] = useState(false)
  const hasEditPermission = usePermission(PermissionSet.REPORTING_EDIT)
  const { setRange } = useDateRangeContext()

  const getDefaultValues = useCallback(
    (data?: Partial<IPeriod>) => ({
      start_date: data?.start_date ?? '',
      end_date: data?.end_date ?? ''
    }),
    []
  )

  const {
    data: period,
    error: periodError,
    refetch
  } = useQuery({
    queryKey: [slug, 'periods', periodId],
    queryFn: () => fetchPeriod({ slug, periodId })
  })

  const form = useForm({
    defaultValues: async () => {
      const { data } = await refetch()
      return getDefaultValues(data)
    }
  })
  const {
    register,
    handleSubmit,
    setError,
    formState: { isDirty, isSubmitting, errors, isLoading }
  } = form

  React.useEffect(() => {
    if (periodError) {
      onClose()
    }
  }, [onClose, periodError])

  const updateMutation = useMutation<IPeriod, unknown, Partial<IPeriod>>({
    mutationFn: data => updatePeriod({ slug, periodId }, data),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'periods'] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'periods', periodId] })
        toast.success(t('general.success'))
      },
      onError: error => {
        const { status, data } = (error as { status?: number; data?: any }) ?? {}
        status === 400 && setAPIErrors(data, setError)
      }
    }
  })

  const onSubmit = handleSubmit(data => updateMutation.mutateAsync(data))

  const deleteMutation = useMutation({
    mutationFn: () => deletePeriod({ slug, periodId }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'periods'] })
        setShowDelete(false)
        onClose()
        toast.success(t('general.success'))
      },
      onError: error => {
        const { status } = (error as { status?: number }) ?? {}
        if (status == 412) {
          toast.error(t('period.deleteFail412Description'))
        }

        setShowDelete(false)
        onClose()
      }
    }
  })

  const handleViewPeriod = useCallback(() => {
    if (!period) return

    setRange({
      key: 'custom',
      date_from: period.start_date,
      date_to: period.end_date
    })

    history.push(getDocumentsPageUrl(slug))
  }, [history, period, setRange, slug])

  const menuItems = useMemo(() => {
    if (!period) return []

    const items: {
      name?: string
      Icon?: IconType
      onClick?: () => void
      intent?: string
    }[] = []

    if (hasEditPermission && period.is_locked) {
      items.push({
        name: t('period.unlock'),
        Icon: FaUnlock,
        onClick: () => {
          const promise = performPeriodAction({
            slug,
            periodId,
            action: 'unlock'
          })
          toast.promise(promise, {
            loading: t('general.loading'),
            success: t('general.success'),
            error: t('general.error')
          })
          promise.then(() => {
            queryClient.invalidateQueries({ queryKey: [slug, 'periods'] })
          })
        }
      })
    }

    items.push({
      name: t('vatPage.setAsDateFilter'),
      Icon: FaCalendarAlt,
      onClick: handleViewPeriod
    })

    items.push(
      {
        name: t('period.downloadLedger'),
        onClick: () => {
          generateReport({
            business_id: slug,
            type: ReportType.LEDGER,
            columns: [
              {
                date_from: period.start_date,
                date_to: period.end_date
              }
            ]
          })
        }
      },
      {
        name: t('period.downloadJournal'),
        onClick: () => {
          generateReport({
            business_id: slug,
            type: ReportType.JOURNAL,
            columns: [
              {
                date_from: period.start_date,
                date_to: period.end_date
              }
            ]
          })
        }
      },
      {
        name: t('period.downloadBalanceSheet'),
        onClick: () => {
          generateReport({
            business_id: slug,
            type: ReportType.BALANCE_SHEET,
            extend_accounts: true,
            columns: [
              {
                date_at: period.end_date
              }
            ]
          })
        }
      },
      {
        name: t('period.downloadBalanceSheetReconciliation'),
        onClick: () => {
          downloadDocxReport({
            business_id: slug,
            type: ReportType.BALANCE_SHEET_RECONCILIATION,
            extend_accounts: true,
            columns: [
              {
                date_from: period.start_date,
                date_to: period.end_date
              }
            ]
          })
        }
      },
      {
        name: t('period.downloadBalanceSheetReconciliationCsv'),
        onClick: () => {
          downloadCsvReport({
            business_id: slug,
            type: ReportType.BALANCE_SHEET_RECONCILIATION,
            extend_accounts: true,
            columns: [
              {
                date_from: period.start_date,
                date_to: period.end_date
              }
            ]
          })
        }
      },
      {
        name: t('period.downloadIncomeStatement'),
        onClick: () => {
          generateReport({
            business_id: slug,
            type: ReportType.INCOME_STATEMENT,
            extend_accounts: true,
            columns: [
              {
                date_from: period.start_date,
                date_to: period.end_date
              }
            ]
          })
        }
      }
    )

    if (hasEditPermission) {
      items.push({
        name: t('period.delete'),
        Icon: FaTrash,
        onClick: () => setShowDelete(true),
        intent: 'danger'
      })
    }

    return items
  }, [handleViewPeriod, hasEditPermission, period, periodId, queryClient, setShowDelete, slug, t])

  if (!period) return null

  return (
    <StyledWrapper data-key={`period-${period.id}`}>
      <HeaderBlock
        header={t('period.period')}
        subHeader={
          <DateBlock>
            {formatDate(period.start_date)} - {formatDate(period.end_date)}
          </DateBlock>
        }
        menuItems={menuItems}
      />

      <ContentWrapper>
        <AnimatedContentLoader isLoading={isLoading} contentDisplay="flex">
          <Tabs>
            <Tab key="details" name={t('period.tabGeneral')}>
              <ScrollableTabContent>
                <Alert
                  type="info"
                  title={t('period.info.notEndedTitle')}
                  isVisible={!period.has_ended}
                  description={t('period.info.notEndedDescription')}
                />
                <Alert
                  type="info"
                  title={t('period.info.isLockedTitle')}
                  isVisible={period.is_locked}
                  description={t('period.info.isLockedDescription')}
                />
                {/*
          <Alert
            type="warning"
            title={t('period.info.notReportedTitle')}
            isVisible={period.has_ended && !period.is_reported}
            description={t('period.info.notReportedDescription')}
          />
          <Alert
            type="success"
            title={t('period.info.isReportedTitle')}
            isVisible={period.is_reported}
            description={t('period.info.isReportedDescription')}
          />
          */}

                <form onSubmit={onSubmit}>
                  <fieldset disabled={period.is_locked || !hasEditPermission}>
                    <Input
                      type="date"
                      label={t('period.form.startDate')}
                      info={t('period.form.startDateInfo')}
                      {...register('start_date', { required: true })}
                      errors={errors.start_date}
                      data-test="period-start-date"
                    />

                    <Input
                      type="date"
                      label={t('period.form.endDate')}
                      info={t('period.form.endDateInfo')}
                      {...register('end_date', { required: true })}
                      errors={errors.end_date}
                      data-test="period-end-date"
                    />

                    {!period.is_locked && hasEditPermission && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          type="submit"
                          intent="success"
                          disabled={!isDirty || isLoading}
                          showSpinner={isSubmitting}
                          icon={<FaCheckCircle />}
                          data-test="period-save-button"
                        >
                          {t('general.save')}
                        </Button>
                      </div>
                    )}
                  </fieldset>
                </form>

                <PermissionBoundary required={PermissionSet.REPORTING_EDIT}>
                  <DatevPeriodComingSoon />
                  <DetailContainer>
                    <ToggleContainer
                      title={t('period.makeFinancialStatementTitle')}
                      openByDefault={true}
                    >
                      {/*<StepperInfo>{t('period.makeFinancialStatementDescription')}</StepperInfo>*/}
                      <PeriodSteps period={period} />
                    </ToggleContainer>
                  </DetailContainer>
                </PermissionBoundary>
              </ScrollableTabContent>
            </Tab>

            <FeatureWrapper feature="financial_statement">
              <Tab key="financial-statement" name={t('period.tabFinancialStatement')}>
                <FinancialStatementForm period={period} />
              </Tab>
            </FeatureWrapper>
          </Tabs>
        </AnimatedContentLoader>
      </ContentWrapper>

      <Prompt
        title={t('period.ensureDeleteTitle')}
        description={t('period.ensureDeleteDescription')}
        isVisible={showDelete}
        onClose={() => setShowDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowDelete(false)
          },
          {
            text: t('period.delete'),
            action: () => deleteMutation.mutateAsync(),
            intent: 'danger'
          }
        ]}
      />
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: white;
  display: flex;
  flex-direction: column;

  .tabs-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .tabs-tab {
    flex: 1;
    overflow: hidden;
  }
`

const ScrollableTabContent = styled.div`
  height: 100%;
  overflow: auto;
`

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md}rem;
  overflow: hidden;
`
